import React, { useEffect, useState, useRef, useCallback } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import i18next from 'i18next';
import { AnnotationIcon, XIcon } from '@heroicons/react/outline';
import '../styles/campaignNotes.css';

const db = firebase.firestore();

const MIN_HEIGHT = 200;
const MAX_HEIGHT = window.innerHeight - 120;
const MIN_WIDTH = 220;
const MAX_WIDTH = window.innerWidth - 48;

const CampaignNotes = ({ campaignUid }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [height, setHeight] = useState(380);
  const [width, setWidth] = useState(320);
  const debounceRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    if (!campaignUid) return;
    unsubscribeRef.current = db.collection('campaigns').doc(campaignUid).onSnapshot(snap => {
      const data = snap.data();
      if (data && data.notes !== undefined) setNotes(data.notes);
    });
    return () => {
      unsubscribeRef.current && unsubscribeRef.current();
      clearTimeout(debounceRef.current);
    };
  }, [campaignUid]);

  const handleChange = (e) => {
    const val = e.target.value;
    setNotes(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      db.collection('campaigns').doc(campaignUid).update({ notes: val });
    }, 1000);
  };

  const onResizeHeight = useCallback((e) => {
    e.preventDefault();
    const startY = e.clientY ?? e.touches?.[0]?.clientY;
    const startHeight = height;

    const onMove = (e) => {
      const y = e.clientY ?? e.touches?.[0]?.clientY;
      setHeight(Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, startHeight + (startY - y))));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
  }, [height]);

  const onResizeWidth = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX ?? e.touches?.[0]?.clientX;
    const startWidth = width;

    const onMove = (e) => {
      const x = e.clientX ?? e.touches?.[0]?.clientX;
      setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + (startX - x))));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
  }, [width]);

  return (
    <>
      <button className="notesfab" onClick={() => setOpen(o => !o)} title={i18next.t('notes.title')}>
        <AnnotationIcon style={{ width: 32, height: 32, color: '#fff', strokeWidth: 2.2 }} />
      </button>

      {open && (
        <div className="notesDrawer" style={{ height, width }}>
          <div className="notesResizeHandle" onMouseDown={onResizeHeight} onTouchStart={onResizeHeight} />
          <div className="notesResizeHandleLeft" onMouseDown={onResizeWidth} onTouchStart={onResizeWidth} />
          <div className="notesDrawerHeader">
            <span className="notesDrawerTitle">{i18next.t('notes.title')}</span>
            <button className="notesCloseBtn" onClick={() => setOpen(false)}>
              <XIcon style={{ width: 18, height: 18 }} />
            </button>
          </div>
          <textarea
            className="notesTextarea"
            value={notes}
            onChange={handleChange}
            placeholder={i18next.t('notes.placeholder')}
          />
        </div>
      )}
    </>
  );
};

export default CampaignNotes;
